/**
 * Link Primitive
 * Semantic link component with design system tokens
 */

import React from "react";
import { Link as ChakraLink } from "@chakra-ui/react";
import { Color } from "../foundation/Color";
import { useColorModeValue } from "../hooks/useColorModeValue";

// Branded types for link tokens
declare const __link_variant_brand: unique symbol;
declare const __link_size_brand: unique symbol;

export interface LinkVariantToken {
  readonly [__link_variant_brand]: true;
  readonly _value: string;
}

export interface LinkSizeToken {
  readonly [__link_size_brand]: true;
  readonly _value: string;
}

// Factory functions
const createLinkVariantToken = (value: string): LinkVariantToken => ({ _value: value } as LinkVariantToken);
const createLinkSizeToken = (value: string): LinkSizeToken => ({ _value: value } as LinkSizeToken);

export interface LinkProps {
  children: React.ReactNode;
  href?: string;
  to?: string; // For React Router integration
  variant?: LinkVariantToken;
  size?: LinkSizeToken;
  isExternal?: boolean;
  isDisabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  target?: string;
  rel?: string;
  "data-testid"?: string;
}

function LinkComponent({
  children,
  href,
  to,
  variant,
  size,
  isExternal = false,
  isDisabled = false,
  onClick,
  target,
  rel,
  "data-testid": testId,
}: LinkProps) {
  // Set defaults inside the component to avoid circular reference issues
  const resolvedVariant = variant || linkTokens.variant.default;
  const resolvedSize = size || linkTokens.size.medium;

  // Get variant colors
  const getVariantColors = (variant: LinkVariantToken) => {
    switch (variant._value) {
      case "primary":
        return {
          color: useColorModeValue(Color.interactive.primary),
          _hover: { color: useColorModeValue(Color.interactive.primaryHover) },
          _active: { color: useColorModeValue(Color.interactive.primaryActive) },
        };
      case "secondary":
        return {
          color: useColorModeValue(Color.text.secondary),
          _hover: { color: useColorModeValue(Color.text.primary) },
        };
      case "danger":
        return {
          color: useColorModeValue(Color.primitive.red(500)),
          _hover: { color: useColorModeValue(Color.primitive.red(600)) },
        };
      case "default":
      default:
        return {
          color: useColorModeValue(Color.text.primary),
          _hover: { color: useColorModeValue(Color.interactive.primary) },
        };
    }
  };

  const getSizeProps = (size: LinkSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          fontSize: "sm",
        };
      case "large":
        return {
          fontSize: "lg",
        };
      case "medium":
      default:
        return {
          fontSize: "md",
        };
    }
  };

  const variantColors = getVariantColors(resolvedVariant);
  const sizeProps = getSizeProps(resolvedSize);

  return (
    <ChakraLink
      {...variantColors}
      {...sizeProps}
      href={href}
      as={to ? "span" : undefined} // For React Router integration
      isExternal={isExternal}
      onClick={isDisabled ? undefined : onClick}
      target={target}
      rel={rel}
      data-testid={testId}
      textDecoration="underline"
      _hover={{
        ...variantColors._hover,
        textDecoration: "underline",
      }}
      _focus={{
        outline: "2px solid",
        outlineColor: useColorModeValue(Color.interactive.primary),
        outlineOffset: "2px",
      }}
      cursor={isDisabled ? "not-allowed" : "pointer"}
      opacity={isDisabled ? 0.6 : 1}
      pointerEvents={isDisabled ? "none" : "auto"}
    >
      {children}
    </ChakraLink>
  );
}

// Link tokens
const linkTokens = {
  variant: {
    default: createLinkVariantToken("default"),
    primary: createLinkVariantToken("primary"),
    secondary: createLinkVariantToken("secondary"),
    danger: createLinkVariantToken("danger"),
  } as const,

  size: {
    small: createLinkSizeToken("small"),
    medium: createLinkSizeToken("medium"),
    large: createLinkSizeToken("large"),
  } as const,
};

// Public API - combines tokens and component
export const Link = Object.assign(LinkComponent, linkTokens);
